<template>
    <div
        v-if="!chat.isQuickOptionHidden"
        :class="componentClasses"
    >
        <div
            v-for="(option, index) in availableQuickOptions"
            :key="option.id.value"
            class="delayed-quick-options__option"
            :style="{ animationDelay: `${index * .1}s` }"
            @click="onOptionClicked(option)"
            @keydown.enter="onOptionClicked(option)"
        >
            {{ localizedQuickOptionShortMessage(option) }}
        </div>

        <div
            class="delayed-quick-options__close-icon"
            @click.stop="manuallyHideButton"
            @keydown.esc="manuallyHideButton"
        >
            <close-icon />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import CloseIcon from '@keyvue/one-awesome-icons/icons/solid/xmark.svg?component';
    import useConfiguration from '@/composables/useConfiguration';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import { type QuickOption, VisitorInteractionType } from '@keyvue/one-api-client';
    import { useI18n } from 'vue-i18n';
    import Chat from '@/chat/Chat';
    import useVisitorData from '@/composables/useVisitorData';

    const chat = Chat.getInstance();
    const { registerInteraction } = useVisitorData();
    const { assistent } = useConfiguration();
    const { state } = useWidgetState();
    const { locale } = useI18n();

    const show = ref(false);
    const openAfterSeconds = assistent.value?.widget?.openAfterSeconds ?? 0;
    const availableQuickOptions = computed(() => assistent.value?.widget.quickOptions ?? []);

    function localizedQuickOptionShortMessage(option: QuickOption): string {
        return assistent.value?.behavior.englishSupportEnabled
            ? option.getShortMessageForLocale(locale.value)
            : option.shortMessage;
    }

    function localizedQuickOptionMessage(option: QuickOption): string {
        return assistent.value?.behavior.englishSupportEnabled
            ? option.getMessageForLocale(locale.value)
            : option.message;
    }

    const componentClasses = computed(() => {
        return {
            'delayed-quick-options': true,
            'delayed-quick-options--open': show.value,
        };
    });

    function manuallyHideButton(): void {
        show.value = false;
        chat.hideQuickOptions();
    }

    async function onOptionClicked(option: QuickOption): Promise<void> {
        if (!assistent.value) {
            return;
        }

        state.value = State.Opened;
        await chat.sendMessage(localizedQuickOptionMessage(option));
        await registerInteraction(VisitorInteractionType.QUICK_OPTION_CLICKED_OUTSIDE);
    }

    onMounted(() => {
        if (chat.isQuickOptionHidden) {
            return;
        }

        if (openAfterSeconds > 0) {
            setTimeout(() => {
                show.value = true;
            }, openAfterSeconds * 1000);
        }
    });

    onBeforeUnmount(() => {
        show.value = false;
    });
</script>

<style scoped lang="scss">
    .delayed-quick-options {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 1rem;
        max-width: 30vw;
        margin: 1rem;

        &__option {
            background-color: white;
            color: black;
            box-shadow: rgba(150, 150, 150, 0.3) 3px 3px 3px 0, rgba(150, 150, 150, 0.2) 0 0 0 1px;
            border-radius: 10px;
            font-size: 80%;
            opacity: 0;
            padding: 1rem;
            pointer-events: auto;
            cursor: pointer;
            animation: showBubble 400ms ease;
            animation-fill-mode: forwards;
            transition: box-shadow 200ms ease;

            &:hover {
                box-shadow: rgba(150, 150, 150, 0.3) 0 10px 10px 0, rgba(150, 150, 150, 0.2) 0 0 0 1px;
            }
        }

        &__close-icon {
            position: absolute;
            display: flex;
            right: -12px;
            top: -12px;
            cursor: pointer;
            pointer-events: auto;
            align-items: center;
            justify-content: center;
            background-color: white;
            border-radius: 50%;
            opacity: 0;
            border: 1px solid #96969633;
            width: 24px;
            height: 24px;
            animation: showBubble 400ms ease;
            animation-fill-mode: forwards;
            animation-delay: 1s;

            & > svg {
                color: #a7a7a7;
                width: 16px;
                height: 16px;
            }
        }
    }

    @keyframes showBubble {
        0% {
            opacity: 0;
            transform: scale(0.7) translateY(20px);
        }
        50% {
            opacity: 1;
            transform: scale(1.3) translateY(-20px);
        }
        100% {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }
</style>
